import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

/**
 * FiscalYearContext — global state for the user's selected Fiscal Year.
 *
 * On login we fetch /api/fiscal-years (which auto-seeds the current
 * calendar-year FY if none exist) and default the selection to the FY
 * containing today's date. The selection is persisted in localStorage so a
 * user keeps the same FY across page reloads / tabs.
 *
 * Consumers can call `getDateRange()` to obtain the inclusive
 * { start, end } ISO date range to pass to report APIs.
 */
const STORAGE_KEY = 'crm.selectedFiscalYearId';

const FiscalYearContext = createContext({
  fiscalYears: [],
  selectedFy: null,
  selectedFyId: null,
  currentFyId: null,
  loading: true,
  setSelectedFyId: () => {},
  refresh: async () => {},
  getDateRange: () => ({ start: null, end: null }),
});

export const FiscalYearProvider = ({ children }) => {
  const { user } = useAuth();
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFyId, setSelectedFyIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [currentFyId, setCurrentFyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/fiscal-years');
      const list = res.data?.items || [];
      const cur = res.data?.current_fy_id || null;
      setFiscalYears(list);
      setCurrentFyId(cur);
      // Default selection priority:
      //   1. In-memory state if still valid (user picked it recently).
      //   2. Stored localStorage value if still valid (sticky across reloads).
      //   3. Current calendar-year FY pointer from the server.
      //   4. First item in the list.
      // Critical: we MUST consult localStorage here, not rely solely on the
      // React `prev` argument, because the AuthContext briefly emits a null
      // user during navigation which can transiently null our state — if we
      // fell back to cur/list[0] in that window we'd silently overwrite the
      // user's selection.
      let stored = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
      setSelectedFyIdState((prev) => {
        const candidate =
          (prev && list.some((fy) => fy.id === prev) && prev) ||
          (stored && list.some((fy) => fy.id === stored) && stored) ||
          cur ||
          (list[0]?.id ?? null);
        try {
          if (candidate) localStorage.setItem(STORAGE_KEY, candidate);
        } catch {
          /* storage unavailable */
        }
        return candidate;
      });
    } catch (e) {
      // Silent — FY selector hides itself if list is empty.
      setFiscalYears([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      refresh();
    } else {
      // User is null — could be (a) genuine logout or (b) a transient
      // AuthContext re-validation during navigation. We must NOT zero out
      // the in-memory selection in either case: refresh() already consults
      // localStorage and re-validates against the fresh FY list, so the
      // selection survives both scenarios. If it's a true logout, the next
      // login will trigger refresh() which will re-resolve correctly.
      setLoading(false);
    }
  }, [user?.id, refresh]);

  const setSelectedFyId = useCallback((id) => {
    setSelectedFyIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — selection still works in-memory */
    }
  }, []);

  const selectedFy = useMemo(
    () => fiscalYears.find((fy) => fy.id === selectedFyId) || null,
    [fiscalYears, selectedFyId]
  );

  const getDateRange = useCallback(() => {
    if (!selectedFy) return { start: null, end: null };
    return { start: selectedFy.start_date, end: selectedFy.end_date };
  }, [selectedFy]);

  const value = useMemo(
    () => ({
      fiscalYears,
      selectedFy,
      selectedFyId,
      currentFyId,
      loading,
      setSelectedFyId,
      refresh,
      getDateRange,
    }),
    [fiscalYears, selectedFy, selectedFyId, currentFyId, loading, setSelectedFyId, refresh, getDateRange]
  );

  return (
    <FiscalYearContext.Provider value={value}>
      {children}
    </FiscalYearContext.Provider>
  );
};

export const useFiscalYear = () => useContext(FiscalYearContext);
